import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useNotificationsStore } from '../store/notifications';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { 
  LayoutGrid, 
  BookOpen, 
  BarChart3, 
  Video, 
  Bell, 
  Plus, 
  Menu, 
  Search,
  GraduationCap,
  Settings,
  LogOut,
  Ticket,
  CircleDollarSign,
  ShieldCheck,
  ShieldAlert,
  ChevronDown,
  Sparkles,
  AlertTriangle,
  ArrowRight,
  X,
} from 'lucide-react';
import { InlineLoader } from '../components/ui/PremiumLoader';

// Premium Logo - Original Black/White Gradient
const Logo = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="12" fill="url(#logo-grad-dash)" />
    <path d="M12 14H18V16H14V24H18V26H12V14ZM22 14H28V26H22V24H26V16H22V14ZM16 18H24V22H16V18Z" fill="white" />
    <defs>
      <linearGradient id="logo-grad-dash" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2d2d2d" />
        <stop offset="1" stopColor="#0a0a0a" />
      </linearGradient>
    </defs>
  </svg>
);

export default function DashboardLayout() {
  const { user, logout, switchUserRole } = useAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationsStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleUpgradeToProfessor = async () => {
    setIsUpgrading(true);
    try {
      await switchUserRole('PROFESSOR');
      toast.success('Congratulations! Your account has been upgraded to Professor.');
      setIsUpgradeModalOpen(false);
      // Navigate to professor dashboard
      navigate('/professor');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to upgrade account';
      toast.error(message);
    } finally {
      setIsUpgrading(false);
    }
  };

  // Check if professor is verified
  const isVerified = user?.verificationStatus === 'VERIFIED';
  const isPendingVerification = user?.verificationStatus === 'PENDING';

  const isProfessor = user?.role === 'PROFESSOR';
  const basePath = isProfessor ? '/professor' : '/student';

  const navigation = isProfessor
    ? [
        { name: 'Dashboard', href: basePath, icon: LayoutGrid },
        { name: 'My Courses', href: `${basePath}/courses`, icon: BookOpen },
        { name: 'Earnings', href: `${basePath}/earnings`, icon: CircleDollarSign },
        { name: 'Analytics', href: `${basePath}/analytics`, icon: BarChart3 },
        { name: 'Live Sessions', href: `${basePath}/meetings`, icon: Video },
      ]
    : [
        { name: 'Dashboard', href: basePath, icon: LayoutGrid },
        { name: 'My Courses', href: `${basePath}/my-courses`, icon: BookOpen },
        { name: 'All Courses', href: `${basePath}/courses`, icon: GraduationCap },
      ];

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return (
    <div className="h-screen flex overflow-hidden bg-[#0a0a0f]">
      {/* Premium Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 flex flex-col
                   transform transition-all duration-500 ease-out
                   lg:translate-x-0 lg:static lg:inset-auto
                   ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ 
          background: 'linear-gradient(180deg, #0f0f14 0%, #0a0a0f 100%)',
        }}
      >
        {/* Logo Section */}
        <div className="flex items-center gap-3 px-6 h-20 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2.5 group">
            <Logo className="w-9 h-9 transition-transform duration-300 group-hover:scale-105" />
            <span className="text-lg font-semibold text-white">
              Ulearning
            </span>
          </Link>
        </div>

        {/* Navigation - Centered, iPad Style */}
        <nav className="flex-1 flex flex-col items-center justify-center px-5 space-y-3 overflow-y-auto scrollbar-hide">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsSidebarOpen(false)}
                className={`group flex items-center gap-4 w-full px-5 py-4 rounded-2xl 
                           transition-all duration-200 ${
                  isActive
                    ? 'text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
                }`}
              >
                <Icon 
                  className={`w-6 h-6 transition-colors duration-200 ${
                    isActive ? 'text-coral' : ''
                  }`} 
                  strokeWidth={isActive ? 2 : 1.5}
                />
                <span className={`font-medium text-base transition-colors duration-200`}>
                  {item.name}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Create Course Button (Professor) */}
        {isProfessor && (
          <div className="px-4 pb-4">
            <Link
              to={`${basePath}/courses/new`}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 
                       rounded-xl font-semibold text-sm text-white
                       transition-all duration-300 hover:scale-[1.02] hover:shadow-glow-coral"
              style={{ 
                background: 'linear-gradient(135deg, #dc464a 0%, #c63b3f 100%)',
                boxShadow: '0 4px 20px rgba(220, 70, 74, 0.3)'
              }}
            >
              <Plus className="w-4 h-4" />
              <span>Create Course</span>
            </Link>
          </div>
        )}

        {/* Bottom Actions - Settings, Support, Logout */}
        <div className="px-5 pb-6 space-y-1" style={{ borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '1rem' }}>
          <Link
            to={`${basePath}/settings`}
            onClick={() => setIsSidebarOpen(false)}
            className={`group flex items-center gap-4 w-full px-5 py-3 rounded-2xl 
                       transition-all duration-200 ${
              location.pathname === `${basePath}/settings`
                ? 'text-white'
                : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
            }`}
          >
            <Settings className={`w-5 h-5 transition-colors duration-200 ${
              location.pathname === `${basePath}/settings` ? 'text-coral' : ''
            }`} strokeWidth={1.5} />
            <span className="font-medium text-sm">Settings</span>
          </Link>
          <Link
            to={`${basePath}/support`}
            onClick={() => setIsSidebarOpen(false)}
            className={`group flex items-center gap-4 w-full px-5 py-3 rounded-2xl 
                       transition-all duration-200 ${
              location.pathname === `${basePath}/support`
                ? 'text-white'
                : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
            }`}
          >
            <Ticket className={`w-5 h-5 transition-colors duration-200 ${
              location.pathname === `${basePath}/support` ? 'text-coral' : ''
            }`} strokeWidth={1.5} />
            <span className="font-medium text-sm">Support</span>
          </Link>
          <button
            onClick={() => { handleLogout(); setIsSidebarOpen(false); }}
            className="group flex items-center gap-4 w-full px-5 py-3 rounded-2xl 
                     text-white/60 hover:text-coral hover:bg-coral/5 transition-all duration-200"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Minimal Top Bar */}
        <header className="flex items-center justify-between h-16 sm:h-20 px-4 sm:px-6 lg:px-8 flex-shrink-0">
          {/* Mobile menu button */}
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="lg:hidden p-2 sm:p-2.5 rounded-xl hover:bg-white/[0.05] transition-colors"
          >
            <Menu className="w-5 h-5 text-white/60" />
          </button>

          {/* Spacer for desktop */}
          <div className="hidden lg:block" />

          {/* Right side - Search & Notification */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Search Bar */}
            <div className="relative hidden lg:block">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
              <input
                type="text"
                placeholder="Search anything..."
                className="w-64 pl-11 pr-4 py-2.5 rounded-xl text-sm text-white 
                         placeholder:text-white/25 transition-all duration-200
                         focus:outline-none focus:ring-1 focus:ring-white/10"
                style={{ 
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)'
                }}
              />
            </div>

            {/* Notification Bell */}
            <Link
              to={`${basePath}/notifications`}
              className="relative p-2.5 rounded-xl hover:bg-white/[0.05] transition-all duration-200 group"
            >
              <Bell className="w-5 h-5 text-white/40 group-hover:text-white/70 transition-colors" />
              {unreadCount > 0 && (
                <span 
                  className="absolute top-1 right-1 w-4 h-4 text-white text-[10px] font-bold 
                           rounded-full flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)' }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-1 sm:gap-2 p-1 sm:p-1.5 sm:pr-3 rounded-xl hover:bg-white/[0.05] transition-all duration-200"
              >
                {user?.avatarUrl ? (
                  <img 
                    src={user.avatarUrl} 
                    alt={user.firstName}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-cover"
                  />
                ) : (
                  <div 
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs sm:text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)' }}
                  >
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </div>
                )}
                <ChevronDown className={`hidden sm:block w-4 h-4 text-white/40 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isProfileOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsProfileOpen(false)} 
                    />
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 w-64 z-50 rounded-xl overflow-hidden"
                      style={{
                        background: 'linear-gradient(180deg, #1a1a1f 0%, #0f0f14 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-white/5">
                        <p className="text-sm font-medium text-white truncate">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-white/40 truncate">{user?.email}</p>
                      </div>

                      {/* Professor Verification Status */}
                      {isProfessor && (
                        <div className="px-2 py-2 border-b border-white/5">
                          <Link
                            to={`${basePath}/verification`}
                            onClick={() => setIsProfileOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                              isVerified 
                                ? 'bg-green-500/10 hover:bg-green-500/20' 
                                : isPendingVerification
                                ? 'bg-yellow-500/10 hover:bg-yellow-500/20'
                                : 'bg-red-500/10 hover:bg-red-500/20'
                            }`}
                          >
                            {isVerified ? (
                              <ShieldCheck className="w-5 h-5 text-green-400" />
                            ) : isPendingVerification ? (
                              <ShieldAlert className="w-5 h-5 text-yellow-400" />
                            ) : (
                              <ShieldAlert className="w-5 h-5 text-red-400" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${
                                isVerified ? 'text-green-400' : isPendingVerification ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {isVerified ? 'Verified Professor' : isPendingVerification ? 'Verification Pending' : 'Not Verified'}
                              </p>
                              {!isVerified && !isPendingVerification && (
                                <p className="text-xs text-white/40">Click to verify identity</p>
                              )}
                            </div>
                          </Link>
                        </div>
                      )}

                      {/* Become Professor (Students Only) */}
                      {!isProfessor && (
                        <div className="px-2 py-2 border-b border-white/5">
                          <button
                            onClick={() => {
                              setIsProfileOpen(false);
                              setIsUpgradeModalOpen(true);
                            }}
                            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border border-purple-500/20 transition-all"
                          >
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <div className="flex-1 text-left">
                              <span className="text-sm font-medium text-purple-300">Become a Professor</span>
                              <p className="text-xs text-white/40">Create and sell courses</p>
                            </div>
                          </button>
                        </div>
                      )}

                      {/* Menu Items */}
                      <div className="px-2 py-2">
                        <Link
                          to={`${basePath}/settings`}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          <span className="text-sm">Settings</span>
                        </Link>
                        <Link
                          to={`${basePath}/support`}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                        >
                          <Ticket className="w-4 h-4" />
                          <span className="text-sm">Support</span>
                        </Link>
                      </div>

                      {/* Logout */}
                      <div className="px-2 py-2 border-t border-white/5">
                        <button
                          onClick={() => { handleLogout(); setIsProfileOpen(false); }}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm">Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page content - Full height scroll */}
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
          <Outlet />
        </main>
      </div>

      {/* Upgrade to Professor Modal */}
      <AnimatePresence>
        {isUpgradeModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => !isUpgrading && setIsUpgradeModalOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-md mx-4 bg-void border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-white">
                    Become a Professor
                  </h2>
                </div>
                <button
                  onClick={() => setIsUpgradeModalOpen(false)}
                  disabled={isUpgrading}
                  className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                <div>
                  <p className="text-white/70 text-sm">
                    Upgrade your account to start creating and selling courses on Ulearning. 
                    As a professor, you'll be able to:
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-white/60">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      Create unlimited courses
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      Earn money from your content
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      Host live sessions with students
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                      Access analytics and insights
                    </li>
                  </ul>
                </div>

                {/* Warning */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-200 font-medium">This is a permanent change</p>
                    <p className="text-amber-200/70 mt-1">
                      Once you become a professor, you cannot switch back to a student-only account. 
                      This protects your courses and any students who enroll in them.
                    </p>
                  </div>
                </div>

                {/* Note about verification */}
                <p className="text-xs text-white/40">
                  After upgrading, you'll need to complete identity verification before you can publish paid courses.
                </p>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-white/10 bg-white/[0.02] flex gap-3">
                <button
                  onClick={() => setIsUpgradeModalOpen(false)}
                  disabled={isUpgrading}
                  className="flex-1 px-4 py-3 rounded-xl font-medium text-sm text-white/70 hover:text-white hover:bg-white/5 border border-white/10 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgradeToProfessor}
                  disabled={isUpgrading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium text-sm text-white transition-all disabled:opacity-50"
                  style={{ 
                    background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                    boxShadow: '0 4px 20px rgba(147, 51, 234, 0.3)'
                  }}
                >
                  {isUpgrading ? (
                    <>
                      <InlineLoader className="mr-1" />
                      <span>Upgrading...</span>
                    </>
                  ) : (
                    <>
                      <span>Upgrade Now</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
